<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap cart-page">
        <h1 class="capitalize-text">{{ $t('shopping_cart.shopping_cart') }}</h1>
        <div class="row items-end q-col-gutter-lg">
            <div class="col-12 col-md-9">
                <div class="cart-list">
                    <div class="row justify-between">
                        <h6>
                            {{
                                $tc(
                                    'shopping_cart.items_in_shopping_cart',
                                    cartItemsNumber
                                )
                            }}
                        </h6>
                        <h6
                            v-if="cart.length"
                            class="cart-actions"
                            @click="emptyCart"
                        >
                            <span>{{ $t('actions.empty_cart') }}</span>
                        </h6>
                    </div>
                    <div
                        v-for="(cartItem, index) in cartItems"
                        :key="cartItem.id"
                    >
                        <CourseCartItem
                            :index="index"
                            :cart-item="cartItem"
                            :user="user"
                            @removecourse="removeFromCart"
                            @movetowishlist="moveToWishlist"
                        ></CourseCartItem>
                    </div>
                </div>
            </div>
            <div v-if="cart.length > 0" class="col-12 col-md-3">
                <div class="cart-total">
                    <h6>{{ $t('general.total') }}:</h6>
                    <strong>
                        {{
                            new Intl.NumberFormat(
                                currency.definition['locale'],
                                {
                                    style: 'currency',
                                    currency: currency.key,
                                }
                            ).format(cartTotal)
                        }}
                    </strong>
                    <q-btn
                        class="checkout-btn"
                        color="secondary"
                        size="lg"
                        @click="checkout"
                    >
                        {{ $t('actions.checkout') }}
                    </q-btn>
                </div>
            </div>
        </div>
        <q-separator />
        <h4 class="featured-title">{{ $t('shopping_cart.best_sellers') }}:</h4>
        <div class="courses-wrap">
            <CourseThumb
                v-for="(course, index) in bestSellers"
                :key="index"
                :index="index"
                :course="course"
            />
        </div>
        <h4 class="featured-title">
            {{ $t('shopping_cart.coud_be_interested_in') }}:
        </h4>
        <div class="courses-wrap">
            <CourseThumb
                v-for="(course, index) in courses"
                :key="index"
                :index="index"
                :course="course"
            />
        </div>
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import CourseThumb from '@/components/CourseThumb'
import CourseCartItem from '@/components/CourseCartItem'
import wishlistService from '@/services/modules/wishlist'
import coursesService from '@/services/modules/courses'
import picturesService from '@/services/pictures'
import encoders from '@/helpers/encoders'
import store from '@/store'

const loadingKeys = {
    fetchingCourses: 'fetching-courses',
    addingToWishlist: 'adding-to-wishlist',
    fetchingBestSellers: 'fetching-best-sellers',
    fetchingCart: 'fetching-cart',
}

export default {
    name: 'Cart',

    components: {
        PageLoading,
        CourseThumb,
        CourseCartItem,
    },

    data() {
        return {
            cartItems: [],
            totalPrice: 0,
            courses: [],
            bestSellers: [],
        }
    },

    computed: {
        isLoading() {
            return (
                this.isFetchingCourses ||
                this.isAddingToWishlist ||
                this.isFetchingCart
            )
        },

        isFetchingCourses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCourses
            ).length
        },

        isFetchingCart() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCart
            ).length
        },

        isAddingToWishlist() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.addingToWishlist
            ).length
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        currency() {
            return store.getters['currency/currentCurrency']
        },

        user() {
            return store.getters['session/user']
        },

        cart() {
            return store.getters['cart/cart']
        },

        cartItemsNumber() {
            return store.getters['cart/itemCount']
        },

        cartTotal() {
            let cartTotal = 0
            this.cartItems.forEach((cartItem) => {
                this.mapCartItem(cartItem)
                let total = 0
                if (this.currency) {
                    total =
                        parseFloat(
                            JSON.parse(cartItem.price)[this.currency.key]
                        ) * (cartItem.quantity >= 1 ? cartItem.quantity : 1)
                }
                // if (cartItem.promotion_discount) {
                total = total * (1 - cartItem.promotion_discount / 100)
                total = total * (1 + cartItem.vat.value / 100)
                // }
                cartTotal = cartTotal + total
            })
            return cartTotal.toFixed(2)
        },
    },

    watch: {
        cart() {
            this.fetchCart()
        },
    },

    async created() {
        await this.fetchCart()
        await this.fetchCourses()
        await this.fetchBestSellers()
    },

    methods: {
        async removeFromCart(course) {
            store.dispatch('cart/removeItemFromCart', course)
        },

        emptyCart() {
            store.dispatch('cart/emptyCart')
        },

        async fetchWishlistCount() {
            let parameters = {
                filter: {},
            }

            let response = await wishlistService.count(parameters)

            if (response.status === 200) {
                //update wishlist store counter
                store.dispatch(
                    'wishlist/updateCount',
                    response.data.results[0].count
                )
            }
        },

        async moveToWishlist(course) {
            if (this.user) {
                let response = await this.addToWishlist(course)

                if (response.status === 200) {
                    await this.removeFromCart(course)
                    this.fetchWishlistCount()

                    this.$q.notify({
                        type: 'positive',
                        message: this.$t('success.course_added_to_wishlist'),
                    })
                } else {
                    this.$q.notify({
                        type: 'negative',
                        message: this.$t(
                            'errors.error_adding_course_to_wishlist'
                        ),
                    })
                }
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('errors.user_must_be_logged_in'),
                })
            }
        },

        async addToWishlist(course) {
            this.loadingAdd(loadingKeys.addingToWishlist)

            let parameters = {
                data: [
                    {
                        course_id: course.id,
                        user_id: this.user.id,
                    },
                ],
            }

            let response = await wishlistService.create(parameters)

            this.loadingRemove(loadingKeys.addingToWishlist)

            return response
        },

        mapCartItem(cartItem) {
            let cartStoreItem = this.cart.find(
                (cart) => cart.id === cartItem.id
            )
            if (cartStoreItem) {
                cartItem.quantity = cartStoreItem.quantity
            }
            return cartItem
        },

        async fetchCart() {
            if (this.isFetchingCart) {
                return
            }

            this.loadingAdd(loadingKeys.fetchingCart)

            let courseIds = this.cart.map((a) => parseInt(a.id))

            let parameters = {
                filter: {
                    active: true,
                    published: true,
                    courseList: courseIds,
                },

                relations: {
                    vat: true,
                    courses_coursetype: true,
                    courses_packagetypes: true,
                },
            }

            let response = await coursesService.retrieve(parameters)

            if (response.status === 200) {
                let cartItems = response.data.results

                // Remove any course from vuex and localStorage that didn't come back in this request
                let vuexCartItems = this.cart

                vuexCartItems.forEach((cartItem) => {
                    var found = !!cartItems.find(
                        (responseItem) => responseItem.id == cartItem.id
                    )

                    if (!found) {
                        this.$store.dispatch(
                            'cart/removeItemFromCart',
                            cartItem
                        )
                    }
                })

                await Promise.all(
                    cartItems.map(async (cartItem) => {
                        let imageResponse = await picturesService.retrieveById(
                            cartItem.coursepicture_id
                        )
                        if (imageResponse.status === 200) {
                            cartItem.image = encoders.arrayBufferToBase64(
                                imageResponse
                            )
                        } else {
                            cartItem.image = require('@/assets/logo.svg')
                        }
                    })
                )

                cartItems.forEach((cartItem) => {
                    this.mapCartItem(cartItem)
                })

                this.cartItems = cartItems
            } else {
                //error message
            }

            this.loadingRemove(loadingKeys.fetchingCart)
        },

        async fetchCourses() {
            this.loadingAdd(loadingKeys.fetchingCourses)

            let parameters = {
                filter: {
                    active: true,
                    published: true,
                },
                pagination: {
                    currentPage: 1,
                    perPage: 5,
                },
                relations: {
                    courses_coursetype: true,
                    courses_packagetypes: true,
                },
            }

            let response = await coursesService.retrieve(parameters)

            if (response.status === 200) {
                this.courses = response.data.results
            } else {
                //error message
            }

            this.loadingRemove(loadingKeys.fetchingCourses)
        },

        async fetchBestSellers() {
            this.loadingAdd(loadingKeys.fetchingBestSellers)

            let response = await coursesService.bestsellers()

            if (response.status === 200) {
                this.bestSellers = response.data.results.map((element) => {
                    element.courses_coursetype = {}
                    element.courses_coursetype.slug = element.courseTypeSlug
                    return element
                })
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_message'),
                })
            }

            this.loadingRemove(loadingKeys.fetchingBestSellers)
        },

        checkout() {
            this.$router.push({ name: 'Checkout' })
        },
    },
}
</script>
